import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaEnvelope, FaLock, FaUser, FaPhone } from 'react-icons/fa';
import pb from '../pocketbase';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Login State
    const [loginData, setLoginData] = useState({ email: '', password: '' });

    // Signup State
    const [signupData, setSignupData] = useState({
        email: '',
        password: '',
        passwordConfirm: '',
        name: '',
        telephone: '',
    });

    if (!isOpen) return null;

    const handleLoginChange = (e) => setLoginData({ ...loginData, [e.target.name]: e.target.value });
    const handleSignupChange = (e) => setSignupData({ ...signupData, [e.target.name]: e.target.value });

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await pb.collection('users').authWithPassword(loginData.email, loginData.password);
            onClose();
        } catch (err) {
            setError("Email ou mot de passe incorrect.");
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (signupData.password !== signupData.passwordConfirm) {
            setError("Les mots de passe ne correspondent pas.");
            setLoading(false);
            return;
        }

        try {
            const generateReferralCode = () => {
                return 'RP-' + Math.random().toString(36).substring(2, 6).toUpperCase();
            };

            const formData = new FormData();
            formData.append('username', `user_${Math.random().toString(36).substr(2, 9)}`);
            formData.append('email', signupData.email);
            formData.append('emailVisibility', true);
            formData.append('password', signupData.password);
            formData.append('passwordConfirm', signupData.passwordConfirm);
            formData.append('name', signupData.name);
            formData.append('telephone', signupData.telephone);
            formData.append('referral_code', generateReferralCode());
            formData.append('points_bonus', 0);

            await pb.collection('users').create(formData);
            await pb.collection('users').authWithPassword(signupData.email, signupData.password);

            onClose();
        } catch (err) {
            console.error(err);
            setError("Erreur lors de l'inscription.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-modal-overlay" onClick={onClose}>
            <div className="auth-modal-content" onClick={e => e.stopPropagation()}>
                <button className="auth-close-btn" onClick={onClose}><FaTimes /></button>

                <div className="auth-header">
                    <h2>{isLogin ? 'Bon retour !' : 'Rejoignez-nous'}</h2>
                    <p>{isLogin ? 'Connectez-vous pour accéder à votre espace.' : 'Créez votre compte en quelques secondes.'}</p>
                </div>

                <div className="auth-tabs">
                    <button
                        className={`auth-tab ${isLogin ? 'active' : ''}`}
                        onClick={() => { setIsLogin(true); setError(null); }}
                    >
                        Connexion
                    </button>
                    <button
                        className={`auth-tab ${!isLogin ? 'active' : ''}`}
                        onClick={() => { setIsLogin(false); setError(null); }}
                    >
                        Inscription
                    </button>
                    <div className={`auth-tab-indicator ${isLogin ? 'left' : 'right'}`} />
                </div>

                <div className="auth-body">
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="auth-error"
                        >
                            {error}
                        </motion.div>
                    )}

                    <AnimatePresence mode="wait">
                        {isLogin ? (
                            <motion.form
                                key="login"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                                onSubmit={handleLogin}
                                className="auth-form"
                            >
                                <div className="auth-input-group">
                                    <FaEnvelope className="auth-icon" />
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email"
                                        required
                                        value={loginData.email}
                                        onChange={handleLoginChange}
                                    />
                                </div>
                                <div className="auth-input-group">
                                    <FaLock className="auth-icon" />
                                    <input
                                        type="password"
                                        name="password"
                                        placeholder="Mot de passe"
                                        required
                                        value={loginData.password}
                                        onChange={handleLoginChange}
                                    />
                                </div>
                                <div className="auth-actions">
                                    <a href="#" className="forgot-password">Mot de passe oublié ?</a>
                                </div>
                                <button type="submit" className="auth-submit-btn" disabled={loading}>
                                    {loading ? 'Connexion...' : 'Se connecter'}
                                </button>
                            </motion.form>
                        ) : (
                            <motion.form
                                key="signup"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                onSubmit={handleSignup}
                                className="auth-form"
                            >
                                <div className="auth-input-group">
                                    <FaUser className="auth-icon" />
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Nom complet"
                                        required
                                        value={signupData.name}
                                        onChange={handleSignupChange}
                                    />
                                </div>
                                <div className="auth-input-group">
                                    <FaEnvelope className="auth-icon" />
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email"
                                        required
                                        value={signupData.email}
                                        onChange={handleSignupChange}
                                    />
                                </div>
                                <div className="auth-input-group">
                                    <FaPhone className="auth-icon" />
                                    <input
                                        type="tel"
                                        name="telephone"
                                        placeholder="Téléphone (ex: 0612345678)"
                                        required
                                        value={signupData.telephone}
                                        onChange={handleSignupChange}
                                    />
                                </div>
                                <div className="auth-input-group">
                                    <FaLock className="auth-icon" />
                                    <input
                                        type="password"
                                        name="password"
                                        placeholder="Mot de passe"
                                        required
                                        minLength={8}
                                        value={signupData.password}
                                        onChange={handleSignupChange}
                                    />
                                </div>
                                <div className="auth-input-group">
                                    <FaLock className="auth-icon" />
                                    <input
                                        type="password"
                                        name="passwordConfirm"
                                        placeholder="Confirmer mot de passe"
                                        required
                                        value={signupData.passwordConfirm}
                                        onChange={handleSignupChange}
                                    />
                                </div>
                                <button type="submit" className="auth-submit-btn" disabled={loading}>
                                    {loading ? 'Inscription...' : "S'inscrire"}
                                </button>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
